<script setup>
import { ref, onMounted } from 'vue'
import { RouterLink } from 'vue-router'
import { api } from '@/api/client'
import { EP } from '@/api/endpoints'

import ButtonSeed from '@/design/primitives/ButtonSeed.vue'
import IconCopy   from '@/design/icons/IconCopy.vue'
import IconCheck  from '@/design/icons/IconCheck.vue'

const FALLBACK_IP = '213.193.197.191:7777'
const FALLBACK_HERO = {
  title1: 'Survive.',
  titleHighlight: 'Build.',
  title2: 'Dominate.',
  description: 'Welcome to Evoluxu — your ultimate ARK: Survival Ascended server. Experience next-gen survival with improved graphics, enhanced mechanics, and a powerful multiplayer world.',
}

const serverIp = ref(FALLBACK_IP)
const hero     = ref({ ...FALLBACK_HERO })
const copied   = ref(false)
// Hold the hero behind a loader until admin content arrives — otherwise the
// FALLBACK_* defaults flash for a frame and then snap to the admin-managed text.
const loaded   = ref(false)

async function loadContent() {
  try {
    const { data } = await api.get(EP.siteContent)
    if (data?.serverCard?.ip) serverIp.value = data.serverCard.ip
    if (data?.hero) {
      hero.value = {
        title1:         data.hero.title1         || FALLBACK_HERO.title1,
        titleHighlight: data.hero.titleHighlight || FALLBACK_HERO.titleHighlight,
        title2:         data.hero.title2         || FALLBACK_HERO.title2,
        description:    data.hero.description    || FALLBACK_HERO.description,
      }
    }
  } catch {
    // On failure we fall back to the defaults rather than a perpetual spinner.
  } finally {
    loaded.value = true
  }
}

async function copyIp() {
  const text = serverIp.value
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
    } else {
      const ta = document.createElement('textarea')
      ta.value = text
      ta.style.position = 'fixed'
      ta.style.left = '-9999px'
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
    }
    copied.value = true
    setTimeout(() => { copied.value = false }, 2000)
  } catch {}
}

onMounted(loadContent)
</script>

<template>
  <section class="hero">
    <!-- Loader holds the hero until admin content loads, preventing the
         fallback-text flash before the API-managed copy snaps in. -->
    <div v-if="!loaded" class="hero__loading" aria-hidden="true">
      <span class="hero__spinner"></span>
    </div>

    <div v-else class="hero__inner u-rise-in">
      <h1 class="hero__title">
        <span>{{ hero.title1 }}</span>
        <span class="hero__title-em">{{ hero.titleHighlight }}</span>
        <span>{{ hero.title2 }}</span>
      </h1>

      <p class="hero__sub">{{ hero.description }}</p>

      <div class="hero__plate" :class="{ 'hero__plate--copied': copied }">
        <span class="hero__plate-label">SERVER</span>
        <span class="hero__plate-rule"></span>
        <span class="hero__plate-ip">{{ serverIp }}</span>
        <button
          type="button"
          class="hero__plate-btn"
          :aria-label="copied ? 'Copied' : 'Copy server IP'"
          @click="copyIp"
        >
          <IconCheck v-if="copied" class="hero__plate-icon"/>
          <IconCopy  v-else         class="hero__plate-icon"/>
          <span>{{ copied ? 'COPIED' : 'COPY' }}</span>
        </button>
      </div>

      <div class="hero__cta">
        <RouterLink to="/market" custom v-slot="{ navigate }">
          <ButtonSeed size="lg" @click="navigate">Visit Store</ButtonSeed>
        </RouterLink>
        <a href="#how-to-play">
          <ButtonSeed variant="ghost" size="lg">How to Play</ButtonSeed>
        </a>
      </div>
    </div>
  </section>
</template>

<style scoped>
.hero {
  position: relative;
  min-height: calc(100dvh - var(--header-h));
  padding: var(--sp-12) var(--sp-6);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Loader shown while admin hero content is fetched */
.hero__loading {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
}
@keyframes hero-spin { to { transform: rotate(360deg); } }
.hero__spinner {
  display: inline-block;
  width: 38px;
  height: 38px;
  border: 3px solid color-mix(in srgb, var(--gold) 28%, transparent);
  border-top-color: var(--gold);
  border-radius: 50%;
  animation: hero-spin 0.7s linear infinite;
}

.hero__inner {
  position: relative;
  z-index: 2;
  max-width: 920px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sp-6);
  text-align: center;
}

.hero__title {
  margin: 0;
  font-family: var(--font-display);
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  line-height: 1;
  font-size: clamp(40px, 6vw, 88px);
  color: var(--ink);
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0 0.4em;
  text-shadow: 0 2px 18px rgba(0, 0, 0, 0.55);
}
.hero__title-em { color: var(--gold); }

.hero__sub {
  margin: 0;
  max-width: 60ch;
  font-size: clamp(var(--fs-16), 1.2vw, var(--fs-18));
  line-height: 1.55;
  color: var(--ink);
  opacity: 0.95;
  text-shadow: 0 1px 6px rgba(0, 0, 0, 0.7);
}

/* IP plate — stamped metal, centered */
.hero__plate {
  display: inline-flex;
  align-items: stretch;
  background: rgba(11, 10, 8, 0.78);
  border: 1px solid var(--gold);
  border-radius: var(--r-xs);
  font-family: var(--font-mono);
  height: 52px;
  box-shadow: 0 0 0 4px rgba(11, 10, 8, 0.45), var(--shadow-2);
  transition: border-color var(--t-fast) var(--ease-road);
}
.hero__plate--copied { border-color: var(--lichen, var(--gold)); }
.hero__plate-label {
  display: inline-flex;
  align-items: center;
  padding: 0 var(--sp-3);
  font-family: var(--font-display);
  font-size: var(--fs-12);
  letter-spacing: 0.18em;
  color: var(--ink-mute);
}
.hero__plate-rule {
  width: 1px;
  background: var(--ash-strong);
  margin: 8px 0;
}
.hero__plate-ip {
  display: inline-flex;
  align-items: center;
  padding: 0 var(--sp-4);
  font-size: var(--fs-16);
  letter-spacing: 0.04em;
  color: var(--gold);
}
.hero__plate-btn {
  display: inline-flex;
  align-items: center;
  gap: var(--sp-2);
  padding: 0 var(--sp-4);
  font-family: var(--font-display);
  font-size: var(--fs-12);
  letter-spacing: 0.16em;
  color: var(--ink-inverse);
  background: var(--gold);
  border-left: 1px solid var(--gold-deep);
  text-transform: uppercase;
  transition: background var(--t-fast) var(--ease-road);
}
.hero__plate-btn:hover { background: var(--gold-deep); }
.hero__plate-icon { width: 14px; height: 14px; }

.hero__cta {
  display: flex;
  gap: var(--sp-3);
  flex-wrap: wrap;
  justify-content: center;
}
.hero__cta-icon { width: 18px; height: 18px; }

@media (max-width: 720px) {
  .hero { padding: var(--header-h) var(--sp-4) var(--sp-12); }
  .hero__title { font-size: clamp(34px, 9vw, 56px); }
  .hero__plate { height: 46px; }
  .hero__plate-label { display: none; }
  .hero__plate-rule { display: none; }
  .hero__cta { gap: var(--sp-2); }
  .hero__cta > * { flex: 1; min-width: 140px; }
  /* Override ButtonSeed lg size on mobile — full-size buttons read as too chunky */
  .hero__cta :deep(.btn) {
    padding: 11px 18px;
    font-size: var(--fs-14);
  }
}
</style>
